import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Search,
  Zap,
  ArrowRight,
  MessageCircle,
  CreditCard,
  Sparkles,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

interface Step {
  step: string;
  icon: LucideIcon;
  title: string;
  desc: string;
  highlight: string;
}

const STEPS: Step[] = [
  {
    step: "01",
    icon: Search,
    title: "Encuentra",
    desc: "Explora cientos de patinetes filtrando por marca, precio, ciudad o autonomía",
    highlight: "+500 anuncios",
  },
  {
    step: "02",
    icon: MessageCircle,
    title: "Contacta",
    desc: "Chatea en tiempo real con el vendedor, resuelve dudas y negocia el precio",
    highlight: "Chat instantáneo",
  },
  {
    step: "03",
    icon: CreditCard,
    title: "Compra seguro",
    desc: "Pago protegido con Stripe y envío con seguimiento hasta tu puerta",
    highlight: "Pago protegido",
  },
];

const StepCard = ({ step, index, isLast }: { step: Step; index: number; isLast: boolean }) => {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const Icon = step.icon;

  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 150}ms` : "0ms" }}
      className={`group relative transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Tarjeta glassmorphism */}
      <div className="relative h-full overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10">
        {/* Número de fondo gigante */}
        <span
          className="pointer-events-none absolute -right-2 -top-4 select-none font-display text-7xl font-black text-primary/5 transition-colors group-hover:text-primary/10"
          aria-hidden
        >
          {step.step}
        </span>

        {/* Icono con halo y scale-in */}
        <div
          style={{ transitionDelay: visible ? `${index * 150 + 200}ms` : "0ms" }}
          className={`relative inline-flex transition-all duration-500 ease-out ${
            visible ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-50 -rotate-12"
          }`}
        >
          <div
            className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-primary to-accent opacity-30 blur-md transition-opacity group-hover:opacity-60"
            aria-hidden
          />
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30">
            <Icon className="h-6 w-6" />
          </div>
        </div>

        {/* Contenido */}
        <div className="mt-5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold tracking-wider text-primary">
              PASO {step.step}
            </span>
            <span className="h-px flex-1 bg-border" aria-hidden />
          </div>
          <h3 className="mt-2 font-display text-xl font-bold">{step.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>

          <div className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-secondary/15 px-3 py-1 text-xs font-semibold text-secondary-foreground ring-1 ring-secondary/20">
            <CheckCircle2 className="h-3.5 w-3.5 text-secondary" />
            {step.highlight}
          </div>
        </div>
      </div>

      {/* Flecha entre tarjetas (md+) */}
      {!isLast && (
        <div
          style={{ transitionDelay: visible ? `${index * 150 + 500}ms` : "0ms" }}
          className={`pointer-events-none absolute top-1/2 -right-4 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-primary/30 bg-background text-primary shadow-md transition-all duration-500 md:flex ${
            visible ? "opacity-100 scale-100" : "opacity-0 scale-0"
          }`}
          aria-hidden
        >
          <ArrowRight className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

const HowItWorks = () => {
  const { ref: headerRef, visible: headerVisible } = useReveal<HTMLDivElement>();
  const { ref: lineRef, visible: lineVisible } = useReveal<HTMLDivElement>({
    threshold: 0.3,
    rootMargin: "0px 0px -10% 0px",
  });

  return (
    <section className="relative overflow-hidden border-t border-border bg-gradient-to-b from-background via-muted/20 to-background py-20">
      {/* Glow decorativos */}
      <div
        className="pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-primary/20 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-accent/20 blur-3xl"
        aria-hidden
      />

      <div className="container relative">
        {/* Encabezado */}
        <div
          ref={headerRef}
          className={`mx-auto max-w-2xl text-center transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Proceso simple en 3 pasos
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Comprar o vender, así de{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              fácil
            </span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Sin intermediarios, sin sorpresas. Te acompañamos desde la búsqueda hasta la entrega.
          </p>
        </div>

        {/* Línea conectora animada (md+) */}
        <div ref={lineRef} className="relative mt-14">
          <div
            className="pointer-events-none absolute left-0 top-10 hidden h-px origin-left bg-gradient-to-r from-primary via-accent to-primary transition-transform duration-[1400ms] ease-out md:block"
            style={{
              right: 0,
              transform: lineVisible ? "scaleX(1)" : "scaleX(0)",
            }}
            aria-hidden
          />

          <div className="grid gap-6 md:grid-cols-3 md:gap-8">
            {STEPS.map((step, idx) => (
              <StepCard key={step.step} step={step} index={idx} isLast={idx === STEPS.length - 1} />
            ))}
          </div>
        </div>

        {/* CTA inferior */}
        <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg" className="gap-2">
            <Link to="/buscar">
              <Search className="h-4 w-4" /> Empezar a buscar
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="gap-2">
            <Link to="/publicar">
              <Zap className="h-4 w-4" /> Publicar anuncio
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
